import { userManagementApi } from './userManagementService';

// Tipuri pentru informatii clinica
export interface ClinicInfo {
  id: string;
  name: string;
  legalName: string;
  registrationNumber: string;
  taxNumber: string;
  address: {
    street: string;
    city: string;
    county: string;
    postalCode: string;
    country: string;
  };
  contact: {
    phone: string;
    email: string;
    website?: string;
    emergencyPhone?: string;
  };
  workingHours: {
    monday: { open: string; close: string; isOpen: boolean };
    tuesday: { open: string; close: string; isOpen: boolean };
    wednesday: { open: string; close: string; isOpen: boolean };
    thursday: { open: string; close: string; isOpen: boolean };
    friday: { open: string; close: string; isOpen: boolean };
    saturday: { open: string; close: string; isOpen: boolean };
    sunday: { open: string; close: string; isOpen: boolean };
  };
  services: string[];
  specializations: string[];
  facilities: string[];
  logo?: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Tipuri pentru setari financiare
export interface FinancialSettings {
  id: string;
  clinicId: string;
  currency: string;
  taxRate: number;
  paymentMethods: {
    cash: boolean;
    card: boolean;
    bankTransfer: boolean;
    installment: boolean;
  };
  invoiceSettings: {
    prefix: string;
    numbering: 'sequential' | 'date_based';
    template: string;
    includeVAT: boolean;
    paymentTerms: number;
  };
  pricingRules: {
    roundingMethod: 'round' | 'ceil' | 'floor';
    minimumAmount: number;
    maximumDiscount: number;
  };
  accountingIntegration: {
    enabled: boolean;
    provider: string;
    apiKey?: string;
    webhookUrl?: string;
  };
  createdAt: string;
  updatedAt: string;
}

// Tipuri pentru setari programari
export interface AppointmentSettings {
  id: string;
  clinicId: string;
  slotDuration: number; // minutes
  bufferTime: number; // minutes between appointments
  advanceBooking: {
    minimum: number; // hours
    maximum: number; // days
  };
  cancellationPolicy: {
    freeCancellationHours: number;
    noShowPenalty: number;
    rescheduleLimit: number;
  };
  reminders: {
    email: {
      enabled: boolean;
      hoursBefore: number[];
    };
    sms: {
      enabled: boolean;
      hoursBefore: number[];
    };
    push: {
      enabled: boolean;
      hoursBefore: number[];
    };
  };
  autoConfirm: boolean;
  requireDeposit: boolean;
  depositAmount: number;
  createdAt: string;
  updatedAt: string;
}

// Tipuri pentru setari servicii
export interface ServiceSettings {
  id: string;
  clinicId: string;
  defaultDuration: number;
  allowCustomDuration: boolean;
  requireVetAssignment: boolean;
  allowPackageServices: boolean;
  pricingRules: {
    allowDiscounts: boolean;
    allowPriceOverride: boolean;
    minimumPrice: number;
  };
  categories: {
    id: string;
    name: string;
    color: string;
    isActive: boolean;
  }[];
  createdAt: string;
  updatedAt: string;
}

// Tipuri pentru setari stoc
export interface StockSettings {
  id: string;
  clinicId: string;
  lowStockThreshold: number;
  autoReorder: boolean;
  reorderPoint: number;
  inventoryMethod: 'fifo' | 'lifo' | 'average';
  requireBatchTracking: boolean;
  requireExpiryTracking: boolean;
  allowNegativeStock: boolean;
  autoConsumption: boolean;
  suppliers: {
    id: string;
    name: string;
    contact: string;
    isActive: boolean;
  }[];
  locations: {
    id: string;
    name: string;
    description?: string;
    isActive: boolean;
  }[];
  createdAt: string;
  updatedAt: string;
}

// Tipuri pentru setari comunicare
export interface CommunicationSettings {
  id: string;
  clinicId: string;
  email: {
    enabled: boolean;
    smtpHost: string;
    smtpPort: number;
    smtpUser: string;
    smtpPassword: string;
    fromEmail: string;
    fromName: string;
    templates: {
      appointmentConfirmation: string;
      appointmentReminder: string;
      appointmentCancellation: string;
      paymentReceipt: string;
      invoice: string;
    };
  };
  sms: {
    enabled: boolean;
    provider: string;
    apiKey: string;
    senderId: string;
    templates: {
      appointmentReminder: string;
      paymentReminder: string;
    };
  };
  push: {
    enabled: boolean;
    firebaseConfig: {
      apiKey: string;
      authDomain: string;
      projectId: string;
      storageBucket: string;
      messagingSenderId: string;
      appId: string;
    };
  };
  clientPortal: {
    enabled: boolean;
    allowOnlineBooking: boolean;
    allowPaymentOnline: boolean;
    allowDocumentAccess: boolean;
    allowCommunication: boolean;
  };
  createdAt: string;
  updatedAt: string;
}

// Tipuri pentru permisiuni
export interface PermissionSettings {
  id: string;
  clinicId: string;
  roles: {
    id: string;
    name: string;
    description: string;
    permissions: string[];
    isActive: boolean;
  }[];
  defaultPermissions: {
    [key: string]: string[];
  };
  customPermissions: {
    [key: string]: {
      name: string;
      description: string;
      category: string;
    };
  };
  createdAt: string;
  updatedAt: string;
}

// Tipuri pentru integrari
export interface IntegrationSettings {
  id: string;
  clinicId: string;
  integrations: {
    id: string;
    name: string;
    type: 'accounting' | 'payment' | 'communication' | 'medical' | 'inventory';
    provider: string;
    isEnabled: boolean;
    config: {
      [key: string]: any;
    };
    lastSync?: string;
    status: 'active' | 'inactive' | 'error';
  }[];
  webhooks: {
    id: string;
    name: string;
    url: string;
    events: string[];
    isActive: boolean;
    secret?: string;
  }[];
  apiKeys: {
    id: string;
    name: string;
    key: string;
    permissions: string[];
    expiresAt?: string;
    isActive: boolean;
  }[];
  createdAt: string;
  updatedAt: string;
}

// Tipuri pentru GDPR
export interface GDPRSettings {
  id: string;
  clinicId: string;
  dataRetention: {
    clientData: number; // months
    medicalRecords: number; // years
    financialData: number; // years
    auditLogs: number; // years
  };
  consentManagement: {
    requireExplicitConsent: boolean;
    consentTypes: {
      id: string;
      name: string;
      description: string;
      isRequired: boolean;
    }[];
  };
  dataProcessing: {
    purposes: {
      id: string;
      name: string;
      description: string;
      legalBasis: string;
    }[];
  };
  rights: {
    allowDataExport: boolean;
    allowDataDeletion: boolean;
    allowDataPortability: boolean;
    allowDataRectification: boolean;
  };
  breachNotification: {
    enabled: boolean;
    notificationPeriod: number; // hours
    contactEmail: string;
  };
  dpo: {
    name: string;
    email: string;
    phone: string;
  };
  createdAt: string;
  updatedAt: string;
}

// Tipuri pentru audit log
export interface AuditLog {
  id: string;
  clinicId: string;
  userId: string;
  userName: string;
  action: string;
  resource: string;
  resourceId: string;
  oldValues?: any;
  newValues?: any;
  ipAddress: string;
  userAgent: string;
  timestamp: string;
}

// Tipuri pentru filtre
export interface AuditLogFilters {
  startDate?: string;
  endDate?: string;
  userId?: string;
  action?: string;
  resource?: string;
  page?: number;
  limit?: number;
}

class ClinicSettingsService {
  private baseUrl = '/clinic-settings';

  // Informatii clinica
  async getClinicInfo(clinicId: string): Promise<ClinicInfo> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/info/${clinicId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching clinic info:', error);
      throw error;
    }
  }

  async updateClinicInfo(clinicId: string, data: Partial<ClinicInfo>): Promise<ClinicInfo> {
    try {
      const response = await userManagementApi.put(`${this.baseUrl}/info/${clinicId}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating clinic info:', error);
      throw error;
    }
  }

  // Setari financiare
  async getFinancialSettings(clinicId: string): Promise<FinancialSettings> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/financial/${clinicId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching financial settings:', error);
      throw error;
    }
  }

  async updateFinancialSettings(clinicId: string, data: Partial<FinancialSettings>): Promise<FinancialSettings> {
    try {
      const response = await userManagementApi.put(`${this.baseUrl}/financial/${clinicId}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating financial settings:', error);
      throw error;
    }
  }

  // Setari programari
  async getAppointmentSettings(clinicId: string): Promise<AppointmentSettings> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/appointments/${clinicId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching appointment settings:', error);
      throw error;
    }
  }

  async updateAppointmentSettings(clinicId: string, data: Partial<AppointmentSettings>): Promise<AppointmentSettings> {
    try {
      const response = await userManagementApi.put(`${this.baseUrl}/appointments/${clinicId}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating appointment settings:', error);
      throw error;
    }
  }

  // Setari servicii
  async getServiceSettings(clinicId: string): Promise<ServiceSettings> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/services/${clinicId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching service settings:', error);
      throw error;
    }
  }

  async updateServiceSettings(clinicId: string, data: Partial<ServiceSettings>): Promise<ServiceSettings> {
    try {
      const response = await userManagementApi.put(`${this.baseUrl}/services/${clinicId}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating service settings:', error);
      throw error;
    }
  }

  // Setari stoc
  async getStockSettings(clinicId: string): Promise<StockSettings> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/stock/${clinicId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching stock settings:', error);
      throw error;
    }
  }

  async updateStockSettings(clinicId: string, data: Partial<StockSettings>): Promise<StockSettings> {
    try {
      const response = await userManagementApi.put(`${this.baseUrl}/stock/${clinicId}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating stock settings:', error);
      throw error;
    }
  }

  // Setari comunicare
  async getCommunicationSettings(clinicId: string): Promise<CommunicationSettings> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/communication/${clinicId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching communication settings:', error);
      throw error;
    }
  }

  async updateCommunicationSettings(clinicId: string, data: Partial<CommunicationSettings>): Promise<CommunicationSettings> {
    try {
      const response = await userManagementApi.put(`${this.baseUrl}/communication/${clinicId}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating communication settings:', error);
      throw error;
    }
  }

  // Setari permisiuni
  async getPermissionSettings(clinicId: string): Promise<PermissionSettings> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/permissions/${clinicId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching permission settings:', error);
      throw error;
    }
  }

  async updatePermissionSettings(clinicId: string, data: Partial<PermissionSettings>): Promise<PermissionSettings> {
    try {
      const response = await userManagementApi.put(`${this.baseUrl}/permissions/${clinicId}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating permission settings:', error);
      throw error;
    }
  }

  // Setari integrari
  async getIntegrationSettings(clinicId: string): Promise<IntegrationSettings> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/integrations/${clinicId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching integration settings:', error);
      throw error;
    }
  }

  async updateIntegrationSettings(clinicId: string, data: Partial<IntegrationSettings>): Promise<IntegrationSettings> {
    try {
      const response = await userManagementApi.put(`${this.baseUrl}/integrations/${clinicId}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating integration settings:', error);
      throw error;
    }
  }

  // Setari GDPR
  async getGDPRSettings(clinicId: string): Promise<GDPRSettings> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/gdpr/${clinicId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching GDPR settings:', error);
      throw error;
    }
  }

  async updateGDPRSettings(clinicId: string, data: Partial<GDPRSettings>): Promise<GDPRSettings> {
    try {
      const response = await userManagementApi.put(`${this.baseUrl}/gdpr/${clinicId}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating GDPR settings:', error);
      throw error;
    }
  }

  // Audit log
  async getAuditLogs(clinicId: string, filters?: AuditLogFilters): Promise<{ logs: AuditLog[]; total: number; page: number; limit: number; }> {
    try {
      const params = new URLSearchParams();
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      if (filters?.userId) params.append('userId', filters.userId);
      if (filters?.action) params.append('action', filters.action);
      if (filters?.resource) params.append('resource', filters.resource);
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());

      const response = await userManagementApi.get(`${this.baseUrl}/audit-logs/${clinicId}?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      throw error;
    }
  }

  // Testare integrari
  async testIntegration(clinicId: string, integrationId: string): Promise<{ success: boolean; message: string; }> {
    try {
      const response = await userManagementApi.post(`${this.baseUrl}/integrations/${clinicId}/test/${integrationId}`);
      return response.data;
    } catch (error) {
      console.error('Error testing integration:', error);
      throw error;
    }
  }

  // Backup setari
  async backupSettings(clinicId: string): Promise<Blob> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/backup/${clinicId}`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error backing up settings:', error);
      throw error;
    }
  }

  // Restore setari
  async restoreSettings(clinicId: string, backupFile: File): Promise<void> {
    try {
      const formData = new FormData();
      formData.append('backup', backupFile);
      
      await userManagementApi.post(`${this.baseUrl}/restore/${clinicId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    } catch (error) {
      console.error('Error restoring settings:', error);
      throw error;
    }
  }
}

export const clinicSettingsService = new ClinicSettingsService();
